/** 시간 표기 통일 (리뷰 m9): 1초 미만은 ms, 그 이상은 소수 1자리 초. */
export function fmtMs(ms: number): string {
  if (!Number.isFinite(ms)) return '—';
  return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}초`;
}

/** HH:MM — 대시보드 「기준 시각」 */
export function fmtHM(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * 서버 시각(ISO, UTC 또는 +오프셋) → 이 브라우저의 현지 시각 「YYYY-MM-DD HH:MM[:SS]」.
 * 문자열을 잘라 쓰면 UTC 가 그대로 보여 한국에서 9시간 어긋났다 (2026-10-05).
 */
export function fmtDateTime(s?: string | null, seconds = false): string {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const p = (n: number) => String(n).padStart(2, '0');
  const base = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
  return seconds ? `${base}:${p(d.getSeconds())}` : base;
}

/**
 * 마지막 소리의 받침 — 한글 · 숫자는 그대로, 영문은 l · m · n · ng 로 끝나면 받침으로 본다(「FLORAL을」 「PLEATED를」).
 * 끝의 괄호 · 따옴표 · 문장부호는 건너뛴다 — 「R8 시험 컬렉션 (이름 바꿈)」 은 「바꿈」 으로 읽는다(리뷰 8회차 관찰 ③).
 * 반환: 0 받침 없음, 1 받침(ㄹ 제외), 2 받침 ㄹ
 */
function finalSound(word: string): 0 | 1 | 2 {
  const w = word.trim().replace(/[\s)\]}』」’”'"〉》.,!?·…~-]+$/u, '');
  const ch = w.slice(-1);
  if (!ch) return 0;
  const code = ch.charCodeAt(0);
  if (code >= 0xac00 && code <= 0xd7a3) {
    const jong = (code - 0xac00) % 28;
    return jong === 0 ? 0 : jong === 8 ? 2 : 1;
  }
  if (/[0-9]/.test(ch)) return ch === '1' || ch === '7' || ch === '8' ? 2 : '036'.includes(ch) ? 1 : 0;
  const tail = w.slice(-2).toLowerCase();
  if (tail === 'ng' || /[mn]$/i.test(ch)) return 1;
  if (/l$/i.test(ch)) return 2;
  return 0;
}

/** 받침에 따라 조사를 고른다 — 을/를 · 과/와 · 이/가 처럼 받침 유무로 갈리는 짝. */
export function withJosa(word: string, withFinal: string, withoutFinal: string): string {
  return finalSound(word) ? withFinal : withoutFinal;
}

/** 「으로 / 로」 — 받침이 ㄹ 이거나 없으면 「로」 (「파랑으로」 「노랑으로」 「보라로」 「검정으로」) */
export function withRo(word: string): string {
  return finalSound(word) === 1 ? '으로' : '로';
}

/** 「‘이름’을」 · 「‘이름’를」 — 토스트 문장용 */
export function quoteObj(name: string): string {
  return `‘${name}’${withJosa(name, '을', '를')}`;
}

/** 오늘이면 HH:MM, 아니면 M월 D일 — 최근 기록 · 컬렉션 수정 시각 */
export function fmtWhen(t: number | string, now = new Date()): string {
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return '—';
  const same = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  return same ? fmtHM(d) : `${d.getMonth() + 1}월 ${d.getDate()}일`;
}

/**
 * 글자를 클립보드에 — 사내망 http(보안 컨텍스트 아님)에서는 navigator.clipboard 가 없어 숨은 textarea 로 대신한다.
 * 둘 다 안 되면 false(화면이 실패 토스트).
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text); return true; }
  } catch { /* 아래로 */ }
  // 대체 경로는 숨은 textarea 가 포커스를 가져간다 — 누른 버튼으로 되돌린다(리뷰 8회차 m41 ①: 사내망 http 휴대폰은 늘 이 경로)
  const back = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch { return false; } finally {
    back?.focus({ preventScroll: true });
  }
}
